import React, {
  useRef,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { intlShape } from 'react-intl';

import {
  getShopReviewsRoute,
  getShopDetailsInfoRoute,
  ROUTES,
} from '@services/routes.service';
import { SCROLL_BEHAVIOR, scrollTo } from '@utils/scroll';
import { TooltipContext } from '@providers/TooltipProvider';

import MagicHeader from '@components/MagicHeader';
import FixedHeader from '@components/FixedHeader';
import MenuSearchInput from '@conversion/containers/MenuSearchInput';
import useIsDesktopLayout from '@hooks/useIsDesktopLayout';
import arrow from '@assets/icons/arrow.svg';

import { BUSINESS_TYPE } from '@commons/constants';

import {
  calculateOpacities,
  calculateOpacitiesSearchMode,
} from '@shopDetails/utils/headerOpacities';
import SubHeaderSeparator from '@components/SubHeaderSeparator';
import { CLICK_LOCATION } from '@shopDetails/tracking/actions/trackSectionClicked';
import { EVENTS } from '@shopDetails/tracking/actions/trackMenuSection';
import showSectionMenu from '@shopDetails/utils/menu';
import BrandHeader from '../components/BrandHeader';
import MainHeaderRestaurantOld from '../components/MainHeaderRestaurant/Loadable';
import MainHeaderGroceries from '../components/MainHeaderGroceries/Loadable';
import ContainerMenuButton from '../components/ContainerMenuButton';
import messages from './messages';
import MenuSection from '../../MenuSection';
import {
  Container,
  ContainerExtendedHeader,
  GoBackButton,
  Logo,
  SearchContainer,
  SearchIcon,
  SearchIconContainer,
  SubHeaderContainer,
  Tabs,
  SecondHeaderGroceries,
  TabsContainer,
  SearchModeContainer,
  HeaderWrapper,
} from './components';
import BrandHeaderShimmer from './components/BrandHeaderShimmer';

function ExtendedMenuHeader({
  shop,
  menuFilterSearch,
  menuSections,
  selectedSection,
  filterMenu,
  goBack,
  match: {
    params: { city, restaurant },
  },
  trackProductSearchClicked,
  trackMenuSection,
  extendedMenuSectionChanged,
  trackSectionClicked,
  subHeader,
  goTo,
  intl: { formatMessage },
  businessType,
  isPickUp,
  showShimmer,
}) {
  const headerSearchEl = useRef(null);

  const [searchMode, setSearchMode] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const isDesktopLayout = useIsDesktopLayout();

  const { setTooltip } = useContext(TooltipContext);

  const focusSearch = useCallback(() => headerSearchEl.current?.focus(), []);

  const shopInfoRoute = useMemo(
    () => getShopDetailsInfoRoute({ city, restaurant }),
    [city, restaurant],
  );

  const reviewsRoute = useMemo(
    () => getShopReviewsRoute({ city, restaurant }),
    [city, restaurant],
  );

  const onFocusSearch = useCallback(
    (focus = false) => {
      trackProductSearchClicked();
      setSearchMode(true);
      setTooltip({ open: false });
      if (focus) {
        focusSearch();
        scrollTo({ top: 0, behavior: SCROLL_BEHAVIOR.SMOOTH });
      }
    },
    [setSearchMode, setTooltip, trackProductSearchClicked, focusSearch],
  );

  const onSearchClick = useCallback(() => {
    onFocusSearch(true);
  }, [onFocusSearch]);

  const onCancelSearch = useCallback(() => {
    setSearchMode(false);
    filterMenu('');
    scrollTo({ top: 0, behavior: SCROLL_BEHAVIOR.SMOOTH });
  }, [filterMenu]);

  const onMenuFilterChange = useCallback(search => filterMenu(search), [
    filterMenu,
  ]);

  const selectTab = useCallback(
    tabId => {
      extendedMenuSectionChanged(tabId);
      const sectionIdx = menuSections.findIndex(s => s.id === tabId);
      trackSectionClicked({
        sectionPosition: sectionIdx,
        sectionName: menuSections[sectionIdx].name,
        clickLocation: CLICK_LOCATION.DEFAULT,
      });
    },
    [trackSectionClicked, extendedMenuSectionChanged, menuSections],
  );

  const subHeaderHeight = subHeader?.props?.height;

  const showHeaderRestaurant = useMemo(
    () => BUSINESS_TYPE.RESTAURANT === businessType,
    [businessType],
  );

  const onClickMenuOpen = useCallback(() => {
    setIsMenuOpen(!isMenuOpen);
    trackMenuSection(EVENTS.CLICKED);
  }, [isMenuOpen, trackMenuSection]);

  const onClickMenuClose = useCallback(() => {
    setIsMenuOpen(!isMenuOpen);
    trackMenuSection(EVENTS.CLOSED);
  }, [isMenuOpen, trackMenuSection]);

  const onClickSection = useCallback(
    (idSection, index) => {
      extendedMenuSectionChanged(idSection);
      trackSectionClicked({
        sectionPosition: index,
        sectionName: menuSections[index].name,
        clickLocation: CLICK_LOCATION.MENU_SECTION,
      });
      setIsMenuOpen(!isMenuOpen);
    },
    [extendedMenuSectionChanged, isMenuOpen, menuSections, trackSectionClicked],
  );

  const showBrandHeaderShimmer = useMemo(
    () => !isDesktopLayout && showShimmer,
    [isDesktopLayout, showShimmer],
  );

  const showMenu = useMemo(() => showSectionMenu(menuSections), [menuSections]);

  return (
    <Container
      searchMode={searchMode}
      subHeaderHeight={subHeaderHeight}
      showHeaderGroceries={!showHeaderRestaurant}
      withMargin={isDesktopLayout}
    >
      {showBrandHeaderShimmer ? (
        <BrandHeaderShimmer />
      ) : (
        <BrandHeader
          goBack={goBack}
          show={!searchMode}
          goTo={goTo}
          showActions={showHeaderRestaurant}
        />
      )}
      <MagicHeader
        calculateOpacities={
          searchMode ? calculateOpacitiesSearchMode : calculateOpacities
        }
        mainHeader={
          <>
            {!showHeaderRestaurant && (
              <MainHeaderGroceries
                show={!searchMode}
                shop={shop}
                reviewsRoute={reviewsRoute}
                onSearchClick={onSearchClick}
                searchPlaceholder={formatMessage(
                  messages.searchInputPlaceholder,
                )}
                subHeader={subHeader}
              />
            )}
            {showHeaderRestaurant && (
              <MainHeaderRestaurantOld
                show={!searchMode}
                shop={shop}
                reviewsRoute={reviewsRoute}
                subHeader={subHeader}
                onSelectTab={selectTab}
                onFocusSearch={onFocusSearch}
                onClickMenu={onClickMenuOpen}
                onSearchClick={onSearchClick}
                searchPlaceholder={formatMessage(
                  messages.searchInputPlaceholder,
                )}
                shopInfoRoute={shopInfoRoute}
                showShimmer={showShimmer}
              />
            )}
          </>
        }
        secondHeader={
          !isDesktopLayout ? (
            <>
              {showHeaderRestaurant ? (
                <ContainerExtendedHeader withMargin={isPickUp}>
                  <FixedHeader onClose={goBack} label={shop?.name || ''}>
                    {searchMode ? (
                      <SearchModeContainer>
                        <SearchContainer
                          withTopMargin={!isPickUp}
                          withLeftMargin={isPickUp}
                        >
                          <MenuSearchInput
                            isHeader
                            ref={headerSearchEl}
                            onClick={onFocusSearch}
                            value={menuFilterSearch}
                            onCancel={onCancelSearch}
                            onChange={onMenuFilterChange}
                            placeholder={formatMessage(
                              messages.searchInputPlaceholder,
                            )}
                            autoFocus={searchMode}
                          />
                        </SearchContainer>
                      </SearchModeContainer>
                    ) : (
                      <HeaderWrapper>
                        <TabsContainer withTopMargin={isPickUp}>
                          <SearchIconContainer>
                            <SearchIcon
                              size="small"
                              withShadow={!subHeader}
                              onClick={onFocusSearch}
                            />
                          </SearchIconContainer>
                          {showMenu && (
                            <ContainerMenuButton
                              onClickMenuOpen={onClickMenuOpen}
                            />
                          )}
                          <Tabs
                            tabs={menuSections}
                            onSelect={selectTab}
                            tabSelectedId={selectedSection}
                            swipeable
                          />
                        </TabsContainer>
                      </HeaderWrapper>
                    )}
                  </FixedHeader>
                  <SubHeaderContainer>
                    {subHeader && <SubHeaderSeparator />}
                    {subHeader}
                  </SubHeaderContainer>
                </ContainerExtendedHeader>
              ) : (
                <SecondHeaderGroceries
                  goBack={goBack}
                  onFocusSearch={onFocusSearch}
                  menuFilterSearch={menuFilterSearch}
                  onCancelSearch={onCancelSearch}
                  onMenuFilterChange={onMenuFilterChange}
                  menuSections={menuSections}
                  selectTab={selectTab}
                  selectedSection={selectedSection}
                  subHeader={subHeader}
                  placeholder={formatMessage(messages.searchInputPlaceholder)}
                />
              )}
            </>
          ) : (
            <SearchContainer
              withTopMargin={!isPickUp}
              withLeftMargin={isPickUp}
            >
              <GoBackButton src={arrow} onClick={goBack} />
              <Logo />
              <MenuSearchInput
                isHeader
                ref={headerSearchEl}
                onClick={onFocusSearch}
                value={menuFilterSearch}
                onCancel={onCancelSearch}
                onChange={onMenuFilterChange}
                placeholder={formatMessage(messages.searchInputPlaceholder)}
                autoFocus={searchMode}
                onGoBack={goBack}
                withPlatformHeader
                withMargin={false}
              />
            </SearchContainer>
          )
        }
      />
      {menuSections?.length > 0 && (
        <MenuSection
          open={isMenuOpen}
          onClose={onClickMenuClose}
          onClickSection={onClickSection}
          sections={menuSections}
        />
      )}
    </Container>
  );
}

ExtendedMenuHeader.propTypes = {
  shop: PropTypes.object.isRequired,
  menuFilterSearch: PropTypes.string,
  menuSections: PropTypes.array,
  selectedSection: PropTypes.number,
  filterMenu: PropTypes.func.isRequired,
  goBack: PropTypes.func.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      city: PropTypes.string,
      restaurant: PropTypes.string,
    }),
  }).isRequired,
  trackProductSearchClicked: PropTypes.func.isRequired,
  extendedMenuSectionChanged: PropTypes.func.isRequired,
  trackSectionClicked: PropTypes.func.isRequired,
  trackMenuSection: PropTypes.func.isRequired,
  subHeader: PropTypes.node,
  goTo: PropTypes.string,

  intl: intlShape.isRequired,
  businessType: PropTypes.string,
  isPickUp: PropTypes.bool,
  showShimmer: PropTypes.bool,
};

ExtendedMenuHeader.defaultProps = {
  menuFilterSearch: '',
  goTo: ROUTES.HOME,
  showShimmer: false,
};

export default ExtendedMenuHeader;
